import React, { createContext, useContext, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import BgColorContainer from '../../Layout/BgColorContainer';
import useCursorZone from '../../../hooks/useCursorZone';
import { ConAcento } from '../../../cms/textos';

/** Colapsa espacios dobles (los textos de los JSON vienen copiados de PDFs). */
const limpiar = (texto = '') => String(texto).replace(/\s+/g, ' ').trim();

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Los bloques del detalle alternan crema y negro según los que tenga cada servicio
// (ServicesDetail.jsx), así que cada uno se pinta en los dos tonos
export const ESTILOS = {
    crema: {
        fondo: 'bg-cream text-ink',
        cursor: 'light',
        suave: 'text-ink/60',
        tenue: 'text-ink/50',
        linea: 'bg-ink/30',
        borde: 'border-ink/15',
        trazo: 'bg-ink/15',
        relleno: 'bg-ink text-cream',
        superficie: 'bg-ink/[0.04] ring-1 ring-inset ring-ink/10',
        icono: 'ring-ink/25 text-ink/80',
        boton: 'dark',
    },
    tinta: {
        fondo: 'bg-ink text-cream',
        cursor: 'dark',
        suave: 'text-cream/60',
        tenue: 'text-cream/50',
        linea: 'bg-cream/30',
        borde: 'border-cream/15',
        trazo: 'bg-cream/15',
        relleno: 'bg-cream text-ink',
        superficie: 'bg-cream/[0.04] ring-1 ring-inset ring-cream/10',
        icono: 'ring-cream/25 text-cream/80',
        boton: 'solid',
    },
};

// La tarjeta que se abre a todo el ancho es la firma de la home (Clientes). Repetida
// en cada sección crema se volvía predecible, así que el resto alterna estas dos.
const ROTACION = ['curva', 'diagonal'];

/**
 * Entrada con scroll de una sección crema sobre el negro:
 * - `tarjeta`: tarjeta redondeada que se abre a todo el ancho.
 * - `curva`: el borde superior entra en cúpula y se aplana. Es border-radius, así
 *   que no recorta el contenido.
 * - `diagonal`: el borde superior entra inclinado y se nivela. Cae hacia la derecha
 *   porque los títulos van a la izquierda.
 * - `telon`: se abre desde una franja al centro, con bordes rectos. Nació en
 *   Análisis 360 porque la curva ya se repetía demasiado.
 */
export function entradaCrema(el, tipo) {
    const scrollTrigger = { trigger: el, start: 'top bottom', end: 'top 25%', scrub: true, invalidateOnRefresh: true };

    if (tipo === 'tarjeta') {
        return gsap.fromTo(el,
            { clipPath: 'inset(0% 6% 0% 6% round 48px)' },
            { clipPath: 'inset(0% 0% 0% 0% round 0px)', ease: 'none', scrollTrigger });
    }
    if (tipo === 'curva') {
        const cupula = () => `50% ${Math.round(Math.min(window.innerHeight * 0.18, 180))}px`;
        return gsap.fromTo(el,
            { borderTopLeftRadius: cupula, borderTopRightRadius: cupula },
            { borderTopLeftRadius: '50% 0px', borderTopRightRadius: '50% 0px', ease: 'none', scrollTrigger });
    }
    if (tipo === 'telon') {
        // Se abre desde una línea al centro de la pantalla hacia arriba y abajo
        return gsap.fromTo(el,
            { clipPath: 'inset(45% 0% 45% 0%)' },
            { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', scrollTrigger: { ...scrollTrigger, end: 'top 15%' } });
    }
    if (tipo === 'diagonal') {
        const inclinada = () => `polygon(0px 0px, 100% ${Math.round(Math.min(window.innerWidth * 0.09, 130))}px, 100% 100%, 0px 100%)`;
        return gsap.fromTo(el,
            { clipPath: inclinada },
            { clipPath: 'polygon(0px 0px, 100% 0px, 100% 100%, 0px 100%)', ease: 'none', scrollTrigger });
    }
    return null;
}

/**
 * Entrada de los bloques crema de una página entera. El caso del portafolio usa
 * "ninguna" porque ahí la transición la hacen las capas (PortfolioCase.jsx).
 */
export const EntradaCremaContext = createContext('auto');

/**
 * Sección de un bloque del detalle. En crema entra con `entradaCrema`: con
 * `entrada="auto"` toma la curva o la diagonal según cuántas secciones crema
 * automáticas van antes en la página, así dos seguidas nunca repiten.
 */
export function Bloque({ tono = 'tinta', entrada: entradaPropia, id, etiqueta, className = '', children }) {
    const ref = useRef(null);
    const cursorZone = useCursorZone(ESTILOS[tono].cursor);
    const entradaDePagina = useContext(EntradaCremaContext);
    const entrada = entradaPropia || entradaDePagina;

    useGSAP(() => {
        if (tono !== 'crema' || entrada === 'ninguna') return;
        let tipo = entrada;
        if (entrada === 'auto') {
            const indice = [...document.querySelectorAll('[data-entrada="auto"]')].indexOf(ref.current);
            tipo = ROTACION[Math.max(indice, 0) % ROTACION.length];
        }
        entradaCrema(ref.current, tipo);
    }, { scope: ref, dependencies: [tono, entrada] });

    return (
        <BgColorContainer>
            <section
                ref={ref}
                id={id}
                aria-label={etiqueta}
                data-entrada={tono === 'crema' ? entrada : undefined}
                className={`relative ${ESTILOS[tono].fondo} ${className}`}
                {...cursorZone}
            >
                {children}
            </section>
        </BgColorContainer>
    );
}

/**
 * Ceja de sección: "02 —— Planes y precios". `as`: el elemento HTML que eligió el
 * equipo en "Páginas" (`elemento()` de src/cms/textos.js).
 */
export function Ceja({ as: Tag = 'p', numero, tono = 'tinta', className = '', children, ...rest }) {
    const e = ESTILOS[tono];
    return (
        <Tag className={`flex items-center gap-4 text-xs uppercase tracking-[0.3em] md:text-sm ${e.tenue} ${className}`} {...rest}>
            {numero && <span className="tabular-nums">{numero}</span>}
            <span aria-hidden="true" className={`h-px w-10 ${e.linea}`} />
            <span>{children}</span>
        </Tag>
    );
}

/**
 * Titular con la última palabra en serif itálica: "Planes y *precios*". Si el
 * texto ya trae asteriscos (los de "Páginas" en Sanity), mandan ellos.
 */
export function TituloAcento({ texto, as: Tag = 'h2', className = '', ...rest }) {
    if (String(texto ?? '').includes('*')) {
        return <Tag className={className} {...rest}><ConAcento texto={texto} /></Tag>;
    }
    const t = limpiar(texto);
    const corte = t.lastIndexOf(' ');
    const acento = <span className="font-serif font-normal italic">{t.slice(corte + 1)}</span>;
    return (
        <Tag className={className} {...rest}>
            {corte > 0 ? <>{t.slice(0, corte)} {acento}</> : acento}
        </Tag>
    );
}

export const CLASE_TITULO = 'text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl xl:text-[5.5rem]';
export const CLASE_CONTENEDOR = 'container px-5 md:px-10 2xl:px-0';
